import React, { useEffect, useState } from "react";
import { Col, Container, Row, Card, Button, Form } from "react-bootstrap";
import SelectField from "../components/common/SelectField";
import { clearError, validateData } from "../validators/YupValidations";
import { donateSchema } from "../validators/validatorSchema";
import { formatUSPhone, normalizePhoneForSave } from "../utils/phoneFormat";
import { toastErrorMessage } from "../utils/utils";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import callImg from "../assets/images/contactus/call.svg";
import mailImg from "../assets/images/contactus/mail.svg";
import locationImg from "../assets/images/contactus/location.svg";
import { SET_LOADING } from "../redux/user";
import { useDispatch, useSelector } from "react-redux";
import PaymentStatusModal from "../components/common/PaymentStatusModal";
import { useLocation, useNavigate } from "react-router-dom";

const Contactcarddetails = [
  { id: 1, title: "Phone", text: "+1 (865) 771-9190", img: callImg },
  {
    id: 2,
    title: "Email",
    text: "gujaratisamajoftampabay@gmail.com",
    img: mailImg,
  },
  {
    id: 3,
    title: "Address",
    text: "5513 Lynn Road, Tampa, FL 33624",
    img: locationImg,
  },
];

function Donate() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.USER_DATA);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    country_code: "+1",
    phone: "",
    amount: "",
    message: "",
    member_id: null,
  });

  const [errors, setErrors] = useState({});
  let token = localStorage.getItem("GSTB_token");

  const buildFullName = (user = {}) =>
    [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ");

  const countryCodeOptions = [
    { value: "+1", label: "+1" },
    { value: "+91", label: "+91" },
  ];

  const handleChange = (field, value) => {
    if (field === "phone") {
      if (formData.country_code === "+1") {
        const digits = value.replace(/\D/g, "").slice(0, 10);
        value = formatUSPhone(digits);
      } else {
        value = value.replace(/\D/g, "").slice(0, 15);
      }
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    errors[field] && clearError(setErrors, field);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      phone:
        formData.country_code === "+1"
          ? normalizePhoneForSave(formData.phone)
          : formData.phone.replace(/\D/g, ""),
    };

    const isValid = await validateData(donateSchema, payload, setErrors);
    if (!isValid) return;

    try {
      dispatch(SET_LOADING(true));
      const response = await API.post(api_paths.member.payment.DONATE_CHECKOUT, payload);

      const token = response?.data?.payload?.token;
      if (token) {
        // Converge hosted payment submission
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${process.env.REACT_APP_PAYMENT_GATEWAY_URL}hosted-payments`;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ssl_txn_auth_token";
        input.value = token;

        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      } else {
        toastErrorMessage("Failed to process donation");
      }
    } catch (error) {
      console.error("Donate checkout error:", error);
      toastErrorMessage(error?.response?.data?.message || "Failed to process donation");
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const convergeId = params.get("converge_transaction_id");

    if (status) {
      setPaymentStatus({ status, convergeId });
    }
  }, [location]);

  // Handle closing the modal and cleaning the URL
  const handleCloseModal = () => {
    setPaymentStatus(null);
    navigate(`/donate`, { replace: true });
  };

  const handleRedirect = () => {
    setPaymentStatus(null);
    if (token) {
      navigate(`/transactions`, { replace: true });
    } else {
      navigate(`/donate`, { replace: true });
    }
  };

  useEffect(() => {
    if (!user) return;

    setFormData((prev) => ({
      ...prev,
      full_name: buildFullName(user),
      email: user.email || "",
      country_code: user.country_code || "+1",
      phone: user.country_code === "+1" ? formatUSPhone(user.phone || "") : user.phone || "",
      member_id: user._id,
    }));
  }, [user]);

  return (
    <>
      {/* Header */}
      <section>
        <Container>
          <Row>
            <Col md={12} className="mt-5 contact-header text-center">
              <h1 className="h1">Support Our Community</h1>
              <p>
                Your generous donation helps us strengthen our community, preserve cultural values,
                and support meaningful initiatives.
              </p>
            </Col>
          </Row>
          <hr />
        </Container>
      </section>

      {/* Donation Form */}
      <section className="contact-info overflow-x-hidden">
        <Container className="my-3">
          <Row className="gy-4 gx-5 justify-content-center">
            <Col lg={6}>
              <Card className="contact-form p-md-3 p-1">
                <Card.Body>
                  <Card.Title className="fw-bold mb-3">Donation</Card.Title>

                  <Form onSubmit={handleSubmit} noValidate>
                    {/* Full Name */}
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label-required">Full Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter full name"
                        value={formData.full_name}
                        onChange={(e) => handleChange("full_name", e.target.value)}
                      />
                      {errors.full_name && (
                        <Form.Text className="text-danger">{errors.full_name.message}</Form.Text>
                      )}
                    </Form.Group>

                    {/* Email */}
                    <Form.Group className="mb-3">
                      <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter email id"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                      />
                      {errors.email && (
                        <Form.Text className="text-danger">{errors.email.message}</Form.Text>
                      )}
                    </Form.Group>

                    {/* Phone */}
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label-required">Contact Number <span className="text-danger">*</span></Form.Label>
                      <Row className="g-2">
                        <Col xs={4} md={3}>
                          <SelectField
                            value={countryCodeOptions.find(
                              (o) => o.value === formData.country_code
                            )}
                            options={countryCodeOptions}
                            onChange={(opt) => handleChange("country_code", opt.value)}
                          />
                        </Col>
                        <Col xs={8} md={9}>
                          <Form.Control
                            type="tel"
                            placeholder="Enter phone number"
                            value={formData.phone}
                            onChange={(e) => handleChange("phone", e.target.value)}
                          />
                        </Col>
                      </Row>
                      {errors.phone && (
                        <Form.Text className="text-danger">{errors.phone.message}</Form.Text>
                      )}
                    </Form.Group>
                    {/* amount */}
                    <Form.Group className="mb-3">
                      <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        placeholder="$ 0"
                        value={formData.amount}
                        onChange={(e) => handleChange("amount", e.target.value)}
                      />
                      {errors.amount && (
                        <Form.Text className="text-danger">{errors.amount.message}</Form.Text>
                      )}
                    </Form.Group>
                    {/* Message */}
                    <Form.Group className="mb-4" controlId="message">
                      <Form.Label className="form-label-required">Your Message <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        name="message"
                        as="textarea"
                        rows={6}
                        placeholder="Enter your message"
                        value={formData.message}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            message: e.target.value,
                          });
                          errors.message && clearError(setErrors, "message");
                        }}
                        className="form-control-soft textarea-160 form-control"
                      />
                      {errors.message && (
                        <Form.Text className="text-danger">{errors.message.message}</Form.Text>
                      )}
                    </Form.Group>

                    <Button type="submit" className="btn btn-orange-lg px-5">
                      Donate
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              {Contactcarddetails.map((item) => (
                <Col key={item.id} lg={12} className="mb-4">
                  <Card className="contact-card h-100 border-0 p-3">
                    <Card.Body className="d-flex align-items-center gap-3 p-0">
                      <div className="icon-wrap d-flex align-items-center justify-content-center">
                        <img src={item.img} alt={item.title} className="contactcard-icon" />
                      </div>

                      <div className="contact-text">
                        <Card.Title className="mb-1 fw-semibold">{item.title}</Card.Title>
                        <Card.Text className="fw-normal mb-0 text-muted small">
                          {item.title === "Email" ? (
                            <a
                              href={`mailto:${item.text}`}
                              className="text-muted text-decoration-none"
                            >
                              {item.text}
                            </a>
                          ) : item.title === "Phone" ? (
                            <a
                              href={`tel:${item.text}`}
                              className="text-muted text-decoration-none"
                            >
                              {item.text}
                            </a>
                          ) : (
                            item.text
                          )}
                        </Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Col>
          </Row>
        </Container>
        {paymentStatus && (
          <PaymentStatusModal
            status={paymentStatus?.status}
            convergeId={paymentStatus?.convergeId}
            onClose={handleCloseModal}
            onNavigate={handleRedirect}
            buttonName={""}
            type={"donate"}
          />
        )}
      </section>
    </>
  );
}

export default Donate;
